import React, { useEffect, useState } from "react";
import { useCorporateDesignProjects } from "../useHook";
import Swal from "sweetalert2";
import { MdDelete } from "react-icons/md";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import SimilarItemsModal from "../../../Components/Modals/SimilarItems/SimilarItems";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendPrefixedImageSeoToFormData,
} from "../../../Components/Shared/ImageSeoFields";

function OurProjectCorporateDesign() {
  const {
    getProjectsData,
    addProject,
    getParentProjectCategories,
    updateProject,
    deleteProject,
    toggleProjectStatus,
    updateProjectPosition,
  } = useCorporateDesignProjects();
  const { hasPermission } = usePermission();
  const canSimilar = hasPermission(SystemPermissions.ManageCorporateDesignProject.ADD_SIMILAR);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [ourProjectsData, setOurProjectsData] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [parentCategoryFilter, setParentCategoryFilter] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedProjectForSimilar, setSelectedProjectForSimilar] =
    useState(null);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    offset: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [searchName, setSearchName] = useState("");
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedProjectForPosition, setSelectedProjectForPosition] =
    useState(null);
  const [formData, setFormData] = useState({
    categoryId: "",
    title: "",
    title_ur: "",
    customUrl: "",
    description: "",
    description_ur: "",
    headerImage: null,
    header_image_alt: "",
    header_image_title: "",
  });
  const [projectImages, setProjectImages] = useState([]);
  const [existingProjectImages, setExistingProjectImages] = useState([]);
  const [newImagePositions, setNewImagePositions] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
  const [primaryExistingImageId, setPrimaryExistingImageId] = useState(null);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [charCounts, setCharCounts] = useState({
    description: 0,
  });

  const countCharacters = (text) => {
    if (!text || typeof text !== "string") return 0;
    return text.length;
  };

  const openModalForAdd = () => {
    setEditData(null);
    setIsEditMode(false);
    setEditingProjectId(null);
    resetForm();
    setIsModalOpen(true);
  };

  const openModalForEdit = (project) => {
    setEditData(project);
    setIsEditMode(true);
    setEditingProjectId(project.id);
    loadProjectForEdit(project);
    setIsModalOpen(true);
  };

  const loadData = async () => {
    const params = {
      offset: pagination.offset,
      limit: pagination.limit,
    };
    if (searchName) {
      params.name = searchName;
    }
    const paginationData = await getProjectsData(
      setOurProjectsData,
      parentCategoryFilter || undefined,
      params,
    );
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  useEffect(() => {
    loadData();
    getParentProjectCategories(setParentCategories);
  }, [pagination.offset, pagination.limit]);

  useEffect(() => {
    if (formData.description !== undefined) {
      const charCount = countCharacters(formData.description);
      setCharCounts((prev) => ({
        ...prev,
        description: charCount,
      }));
    }
  }, [formData.description]);

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleToggleStatus = async (projectId) => {
    try {
      const response = await toggleProjectStatus(projectId);
      if (response) {
        Swal.fire({
          title: "Success!",
          text: "Project status has been toggled successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      loadData();
    } catch (error) {
      console.log(error);
      Swal.fire({
        title: "Error!",
        text: "Failed to toggle project status.",
        icon: "error",
      });
    }
  };

  const loadProjectForEdit = async (projectData) => {
    setIsLoadingProject(true);
    try {
      const description = projectData.description || "";
      const charCount = countCharacters(description);

      setFormData({
        categoryId: projectData.categoryId || projectData.category?.id || "",
        title: projectData.title || projectData.name || "",
        title_ur: projectData.title_ur || projectData.name_ur || "",
        description: description,
        description_ur: projectData.description_ur || "",
        customUrl: projectData.customUrl || "",
        headerImage: projectData.headerImage
          ? {
              preview: projectData.headerImage,
              file: null,
            }
          : null,
        header_image_alt: projectData.header_image_alt || "",
        header_image_title: projectData.header_image_title || "",
      });

      // Load existing project images
      if (
        projectData.projectImages &&
        Array.isArray(projectData.projectImages)
      ) {
        setExistingProjectImages(projectData.projectImages);
        // Pre-select all existing images
        setSelectedImageIds(projectData.projectImages.map((img) => img.id));
        const primaryImg = projectData.projectImages.find((img) => img.is_primary);
        if (primaryImg) {
          setPrimaryExistingImageId(primaryImg.id);
          setPrimaryImageIndex(null);
        } else {
          setPrimaryExistingImageId(null);
          setPrimaryImageIndex(0);
        }
      } else {
        setExistingProjectImages([]);
        setSelectedImageIds([]);
        setPrimaryExistingImageId(null);
        setPrimaryImageIndex(0);
      }

      setCharCounts({
        description: charCount,
      });
    } catch (error) {
      console.error("Error loading project for edit:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load project data for editing.",
        icon: "error",
      });
    } finally {
      setIsLoadingProject(false);
    }
  };

  const handleDelete = async (projectId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteProject(projectId);
          loadData();
          Swal.fire({
            title: "Deleted!",
            text: "Project has been deleted successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          console.error("Error deleting project:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to delete project.",
            icon: "error",
          });
        }
      }
    });
  };
  const handleViewProject = (projectId) => {
    navigate(`/our-project-corporate-design/view/${projectId}`);
  };

  const handleSearch = async () => {
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
    const params = {
      offset: 0,
      limit: pagination.limit,
    };
    if (searchName) {
      params.name = searchName;
    }
    const paginationData = await getProjectsData(
      setOurProjectsData,
      parentCategoryFilter || undefined,
      params,
    );
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  const handleReset = async () => {
    setSearchName("");
    setParentCategoryFilter("");
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
    const paginationData = await getProjectsData(setOurProjectsData, undefined, {
      offset: 0,
      limit: pagination.limit,
    });
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  const handlePositionClick = (project) => {
    setSelectedProjectForPosition(project);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (projectId, newPosition) => {
    await updateProjectPosition(projectId, newPosition);
    await loadData();
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedProjectForPosition(null);
  };

  const handleEditProject = (project) => {
    openModalForEdit(project);
  };

  const handleHeaderImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const checkImageResolution = (file) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (img.width === 450 && img.height === 450) {
              resolve(true);
            } else {
              resolve(false);
            }
          };
          img.src = URL.createObjectURL(file);
        });
      };

      const isValid = await checkImageResolution(file);
      if (isValid) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData((prev) => ({
            ...prev,
            headerImage: {
              preview: reader.result,
              file: file,
            },
          }));
        };
        reader.readAsDataURL(file);
      } else {
        Swal.fire({
          title: "Invalid Image Resolution",
          text: `Image "${file.name}" does not have the required resolution of 450x450. Please upload the correct resolution.`,
          icon: "error",
        });
      }
    }
  };

  const handleProjectImagesUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    const input = event.target;
    if (!files.length) return;

    const checkImageResolution = (file) =>
      new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(img.width === 1200 && img.height === 900);
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(false);
        };
        img.src = objectUrl;
      });

    const results = await Promise.all(files.map((file) => checkImageResolution(file)));
    const validFiles = [];
    const rejectedNames = [];
    results.forEach((isValid, index) => {
      if (isValid) validFiles.push(files[index]);
      else rejectedNames.push(files[index].name);
    });
    input.value = "";

    if (rejectedNames.length) {
      Swal.fire({
        title: "Invalid Image Resolution",
        html: `These gallery images must be exactly <b>1200 × 900</b> pixels and were not added:<br/>${rejectedNames
          .map((name) => `• ${name}`)
          .join("<br/>")}`,
        icon: "error",
      });
    }
    if (!validFiles.length) return;

    const imageObjects = await Promise.all(
      validFiles.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                preview: reader.result,
                file,
                name: file.name,
                imageAlt: "",
                imageTitle: "",
              });
            };
            reader.readAsDataURL(file);
          }),
      ),
    );
    setProjectImages((prev) => [...prev, ...imageObjects]);
  };

  const removeProjectImage = (index) => {
    setProjectImages((prev) => prev.filter((_, i) => i !== index));
    setNewImagePositions((prev) => prev.filter((_, i) => i !== index));
    if (primaryImageIndex === index) {
      setPrimaryImageIndex(0);
    } else if (primaryImageIndex > index) {
      setPrimaryImageIndex(prev => prev - 1);
    }
  };

  const handleSetPrimaryNew = (index) => {
    setPrimaryImageIndex(index);
    setPrimaryExistingImageId(null);
  };

  const handleSetPrimaryExisting = (id) => {
    setPrimaryExistingImageId(id);
    setPrimaryImageIndex(null);
  };

  const onExistingImagePositionChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    setExistingProjectImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onExistingImageSeoChange = (index, field, value) => {
    setExistingProjectImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewProjectImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlt" : "imageTitle";
    setProjectImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [key]: value } : img)),
    );
  };

  const onNewImagePositionChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    setNewImagePositions((prev) => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
  };

  const handleImageToggle = (imageId) => {
    setSelectedImageIds((prev) =>
      prev.includes(imageId)
        ? prev.filter((id) => id !== imageId)
        : [...prev, imageId],
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "description") {
      const charCount = countCharacters(value);

      if (charCount > 925) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      setCharCounts((prev) => ({
        ...prev,
        description: charCount,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentCharCount = countCharacters(formData.description);
    if (currentCharCount > 925) {
      Swal.fire({
        title: "Error",
        text: `Description cannot exceed 925 characters. Current count: ${currentCharCount}`,
        icon: "error",
      });
      return;
    }

    if (!formData.headerImage) {
      Swal.fire({
        title: "Error",
        text: "Header image is required. Please upload a header image.",
        icon: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();

      formDataObj.append("title", formData.title);
      if (formData.title_ur) formDataObj.append("title_ur", formData.title_ur);
      formDataObj.append("customUrl", formData.customUrl);
      formDataObj.append("description", formData.description);
      if (formData.description_ur)
        formDataObj.append("description_ur", formData.description_ur);
      formDataObj.append("categoryId", formData.categoryId);

      if (formData.headerImage?.file) {
        formDataObj.append("headerImage", formData.headerImage.file);
      }
      appendPrefixedImageSeoToFormData(
        formDataObj,
        "header_image",
        formData.header_image_alt,
        formData.header_image_title,
      );

      // Add multiple project images
      projectImages.forEach((image, index) => {
        if (image.file) {
          formDataObj.append("projectImages", image.file);
          formDataObj.append(
            "imagePosition",
            newImagePositions[index] != null ? newImagePositions[index] : index + 1
          );
          if (image.imageAlt != null && String(image.imageAlt).trim()) {
            formDataObj.append("imageAlt", String(image.imageAlt).trim());
          }
          if (image.imageTitle != null && String(image.imageTitle).trim()) {
            formDataObj.append("imageTitle", String(image.imageTitle).trim());
          }
        }
      });

      // For edit mode, add keepImageIds and existing image positions
      if (isEditMode) {
        formDataObj.append("keepImageIds", JSON.stringify(selectedImageIds));
        const positions = {};
        const existingImageAlts = {};
        const existingImageTitles = {};
        existingProjectImages.forEach((img) => {
          if (img.position !== undefined && img.position !== null) {
            positions[img.id] = img.position;
          }
          if (img.image_alt != null && String(img.image_alt).trim()) {
            existingImageAlts[img.id] = String(img.image_alt).trim();
          }
          if (img.image_title != null && String(img.image_title).trim()) {
            existingImageTitles[img.id] = String(img.image_title).trim();
          }
        });
        formDataObj.append("existingImagePositions", JSON.stringify(positions));
        if (Object.keys(existingImageAlts).length > 0) {
          formDataObj.append(
            "existingImageAlts",
            JSON.stringify(existingImageAlts),
          );
        }
        if (Object.keys(existingImageTitles).length > 0) {
          formDataObj.append(
            "existingImageTitles",
            JSON.stringify(existingImageTitles),
          );
        }
        if (primaryExistingImageId !== null) {
          formDataObj.append("primaryExistingImageId", primaryExistingImageId);
        }
      }

      if (primaryImageIndex !== null) {
        formDataObj.append("primaryImageIndex", primaryImageIndex);
      }

      let response;
      if (isEditMode) {
        response = await updateProject(editingProjectId, formDataObj);
      } else {
        response = await addProject(formDataObj);
      }

      resetForm();
      setIsModalOpen(false);

      loadData();

      Swal.fire({
        title: "Success!",
        text: isEditMode
          ? "Project has been updated successfully."
          : "Project has been created successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error saving project:", error);
      Swal.fire({
        title: "Error!",
        text: isEditMode
          ? "Failed to update project. Please try again."
          : "Failed to create project. Please try again.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const hasRequiredFields =
      formData.title.trim() !== "" && formData.categoryId !== "";
    const hasHeaderImage = formData.headerImage !== null;

    return hasRequiredFields && hasHeaderImage;
  };

  const resetForm = () => {
    setFormData({
      categoryId: "",
      title: "",
      title_ur: "",
      customUrl: "",
      description: "",
      description_ur: "",
      headerImage: null,
      header_image_alt: "",
      header_image_title: "",
    });
    setProjectImages([]);
    setExistingProjectImages([]);
    setNewImagePositions([]);
    setSelectedImageIds([]);
    setPrimaryImageIndex(0);
    setPrimaryExistingImageId(null);
    setCharCounts({
      description: 0,
    });
    setIsEditMode(false);
    setEditingProjectId(null);
    setEditData(null);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Manage Corporate Design Project</h2>
        <button onClick={openModalForAdd} className="btn btn-primary h-[44px]">
          <FiPlus className="text-white" />
          Add Project
        </button>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={parentCategoryFilter}
          onChange={(e) => setParentCategoryFilter(e.target.value)}
        >
          <option value="">Select parent category</option>
          {parentCategories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Name</th>
                <th className="table-th">Category</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ourProjectsData?.map((item, index) => (
                <tr
                  key={item?.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                  } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="table-td">
                    <button
                      type="button"
                      onClick={() => handlePositionClick(item)}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                    >
                      {item?.position ?? pagination.offset + index + 1}
                    </button>
                  </td>
                  <td className="table-td">{item?.title || item?.name}</td>
                  <td className="table-td">{item?.category?.name}</td>
                  <td className="table-td">
                    <div
                      className={`status-toggle ${item?.status ? "active" : ""}`}
                      onClick={() => handleToggleStatus(item.id)}
                    >
                      <div className="status-toggle-handle"></div>
                    </div>
                  </td>
                  <td className="table-td flex space-x-4">
                    {canSimilar && (
                      <button
                        className="btn btn-action"
                        onClick={() => {
                          setSelectedProjectForSimilar(item.id);
                          setIsSimilarModalOpen(true);
                        }}
                      >
                        <FiPlusCircle className="text-white" />
                        Similar Items
                      </button>
                    )}
                    <button
                      className="btn btn-view "
                      onClick={() => handleViewProject(item.id)}
                    >
                      <IoEye className="text-white" /> View
                    </button>
                    <button
                      className="btn btn-edit text-white"
                      onClick={() => handleEditProject(item)}
                    >
                      <FiEdit2 className="text-white" /> Edit
                    </button>
                    <button
                      className="btn btn-delete text-white"
                      onClick={() => handleDelete(item.id)}
                    >
                      <MdDelete className="text-white" /> Delete
                    </button>
                  </td>
                </tr>
              ))}

              {ourProjectsData?.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    No project records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={pagination.offset}
            limit={pagination.limit}
            total={pagination.totalCount}
            onPageChange={handlePageChange}
            className=""
          />
        </div>
      )}

      {isPositionModalOpen && selectedProjectForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={
            selectedProjectForPosition.position ??
            pagination.offset +
              ourProjectsData.findIndex(
                (p) => p?.id === selectedProjectForPosition?.id,
              ) +
              1
          }
          recordId={selectedProjectForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Project"
        />
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {isLoadingProject ? (
              <div className="p-8 flex justify-center items-center min-h-[400px]">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
              </div>
            ) : (
              <div className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-800">
                    {isEditMode ? "Edit Project" : "Add Project"}
                  </h2>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Header Image Section */}
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Header Image *
                    </label>
                    <div
                      className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-xl transition-colors ${
                        !formData.headerImage
                          ? "border-red-300 bg-red-50 hover:border-red-400"
                          : "border-gray-300 hover:border-blue-500"
                      }`}
                    >
                      <div className="space-y-1 text-center">
                        {formData.headerImage ? (
                          <div className="relative group">
                            <img
                              src={
                                formData.headerImage.preview
                                  ? formData.headerImage.preview
                                  : formData.headerImage
                              }
                              alt="Header preview"
                              className="mx-auto h-48 w-full object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    headerImage: null,
                                  }))
                                }
                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors transform scale-0 group-hover:scale-100 duration-300"
                              >
                                <svg
                                  className="w-6 h-6"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <svg
                              className="mx-auto h-16 w-16 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="header-image"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                              >
                                <span>Upload a header image</span>
                                <input
                                  id="header-image"
                                  name="header-image"
                                  type="file"
                                  className="sr-only"
                                  accept="image/*"
                                  onChange={handleHeaderImageUpload}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, GIF up to 10MB, 450x450 pixels
                              <span className="text-red-400 font-medium">
                                {" "}
                                * Required
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <ImageSeoFields
                      imageAlt={formData.header_image_alt}
                      imageTitle={formData.header_image_title}
                      onAltChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          header_image_alt: value,
                        }))
                      }
                      onTitleChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          header_image_title: value,
                        }))
                      }
                      className="mt-4"
                    />
                  </div>

                  {/* Project specific fields */}
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Parent Category *
                    </label>
                    <select
                      name="categoryId"
                      value={formData.categoryId === "" ? "" : String(formData.categoryId)}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select parent category</option>
                      {parentCategories.map((category) => (
                        <option key={category.id} value={String(category.id)}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <UrduInput
                        label="عنوان"
                        name="title_ur"
                        value={formData.title_ur}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Custom URL
                      </label>
                      <input
                        type="text"
                        name="customUrl"
                        value={formData.customUrl}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter custom URL (e.g., my-project-url)"
                      />
                    </div>
                  </div>

                  {/* Description Section */}
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description (Max 925 characters)
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={6}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                      placeholder="Enter project description..."
                    />
                    <div className="flex justify-end mt-1">
                      <span
                        className={`text-sm ${charCounts.description >= 800 ? "text-red-500" : "text-gray-500"}`}
                      >
                        {charCounts.description}/925 characters
                      </span>
                    </div>
                    <UrduInput
                      label="تفصیل"
                      name="description_ur"
                      value={formData.description_ur}
                      onChange={handleInputChange}
                      type="textarea"
                      rows={4}
                    />
                  </div>

                  {/* Existing Project Images - Only in Edit Mode */}
                  {isEditMode && existingProjectImages.length > 0 && (
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Existing Project Images
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
                        {existingProjectImages.map((image, index) => (
                          <div key={image.id} className="relative group">
                            <div
                              className={`relative h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden transition-all ${
                                primaryExistingImageId === image.id
                                  ? "ring-2 ring-blue-500"
                                  : ""
                              } ${
                                !selectedImageIds.includes(image.id)
                                  ? "opacity-40"
                                  : ""
                              }`}
                            >
                              <img
                                src={image.image_url}
                                alt={`Project ${image.id}`}
                                className="h-full w-full object-cover"
                              />

                              {/* Primary badge */}
                              {primaryExistingImageId === image.id && selectedImageIds.includes(image.id) && (
                                <span className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-20 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                </span>
                              )}

                              {/* Set Primary Button for existing images */}
                              {selectedImageIds.includes(image.id) && primaryExistingImageId !== image.id && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetPrimaryExisting(image.id);
                                  }}
                                  className="absolute bottom-1 left-1 p-1 rounded-full bg-white text-gray-600 hover:bg-blue-100 transition-all opacity-0 group-hover:opacity-100 shadow-md z-20"
                                  title="Set as Primary Image"
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                    />
                                  </svg>
                                </button>
                              )}

                              {/* Marked for deletion label */}
                              {!selectedImageIds.includes(image.id) && (
                                <div className="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-50 z-10">
                                  <span className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                                    Will be deleted
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Remove/Restore Button */}
                            <button
                              type="button"
                              onClick={() => handleImageToggle(image.id)}
                              className={`absolute -top-2 -right-2 p-1 rounded-full transition-all shadow-md z-20 ${
                                !selectedImageIds.includes(image.id)
                                  ? "bg-green-500 hover:bg-green-600"
                                  : "bg-red-500 hover:bg-red-600 opacity-0 group-hover:opacity-100"
                              } text-white`}
                              title={
                                selectedImageIds.includes(image.id)
                                  ? "Delete image"
                                  : "Restore image"
                              }
                            >
                              {selectedImageIds.includes(image.id) ? (
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                              )}
                            </button>

                            {/* Position Input */}
                            {selectedImageIds.includes(image.id) && primaryExistingImageId !== image.id ? (
                              <div className="mt-1">
                                <label className="block text-[10px] text-gray-600 mb-0.5">
                                  Position
                                </label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[1-9][0-9]*"
                                  value={image.position || ""}
                                  onChange={(e) => onExistingImagePositionChange(index, e.target.value)}
                                  className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded"
                                  placeholder="1"
                                />
                              </div>
                            ) : selectedImageIds.includes(image.id) && primaryExistingImageId === image.id ? (
                              <p className="mt-1 text-[10px] text-blue-600 text-center font-medium">
                                Primary (not indexed)
                              </p>
                            ) : null}
                            {selectedImageIds.includes(image.id) && (
                              <ImageSeoFields
                                stacked
                                imageAlt={image.image_alt || ""}
                                imageTitle={image.image_title || ""}
                                onAltChange={(value) =>
                                  onExistingImageSeoChange(index, "image_alt", value)
                                }
                                onTitleChange={(value) =>
                                  onExistingImageSeoChange(
                                    index,
                                    "image_title",
                                    value,
                                  )
                                }
                                className="mt-2"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        Hover over an image and click the X button to mark it
                        for deletion
                      </p>
                    </div>
                  )}

                  {/* Add New Project Images */}
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Project Images
                    </label>
                    <label
                      htmlFor="project-images"
                      className="mt-1 flex flex-col justify-center items-center px-6 py-8 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                    >
                      <svg
                        className="h-16 w-16 text-gray-400 mb-3"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Click to select images
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF · Multiple images · Exactly 1200 × 900
                      </p>
                      <input
                        id="project-images"
                        name="project-images"
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        multiple
                        onChange={handleProjectImagesUpload}
                      />
                    </label>

                    {/* Preview New Project Images */}
                    {projectImages.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-2">
                          {projectImages.length} new image(s) selected
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {projectImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <div
                                className={`relative h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${
                                  index === primaryImageIndex
                                    ? "ring-2 ring-blue-500"
                                    : ""
                                }`}
                              >
                                <img
                                  src={image.preview}
                                  alt={image.name}
                                  className="h-full w-full object-cover"
                                />
                                
                                {/* Primary badge */}
                                {primaryImageIndex === index && (
                                  <span className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-20 flex items-center gap-1">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  </span>
                                )}

                                {/* Set Primary Button */}
                                {index !== primaryImageIndex && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetPrimaryNew(index);
                                    }}
                                    className="absolute bottom-1 left-1 p-1 rounded-full bg-white text-gray-600 hover:bg-blue-100 transition-all opacity-0 group-hover:opacity-100 shadow-md z-20"
                                    title="Set as Primary Image"
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              
                              {/* Remove button */}
                              <button
                                type="button"
                                onClick={() => removeProjectImage(index)}
                                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 z-20"
                              >
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>

                              {/* Position Input */}
                              {index !== primaryImageIndex ? (
                                <div className="mt-1">
                                  <label className="block text-[10px] text-gray-600 mb-0.5">
                                    Position
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[1-9][0-9]*"
                                    value={newImagePositions[index] || ""}
                                    onChange={(e) => onNewImagePositionChange(index, e.target.value)}
                                    className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded"
                                    placeholder="1"
                                  />
                                </div>
                              ) : (
                                <p className="mt-1 text-[10px] text-blue-600 text-center font-medium">
                                  Primary (not indexed)
                                </p>
                              )}
                              
                              <p className="text-[10px] text-gray-500 mt-0.5 truncate text-center" title={image.name}>
                                {image.name}
                              </p>
                              <ImageSeoFields
                                stacked
                                imageAlt={image.imageAlt || ""}
                                imageTitle={image.imageTitle || ""}
                                onAltChange={(value) =>
                                  onNewProjectImageSeoChange(index, "image_alt", value)
                                }
                                onTitleChange={(value) =>
                                  onNewProjectImageSeoChange(index, "image_title", value)
                                }
                                className="mt-2"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="btn btn-reset-filter justify-center"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`btn justify-center ${
                        isEditMode ? "btn-edit text-white" : "btn-primary"
                      } ${
                        isSubmitting || !isFormValid()
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={isSubmitting || !isFormValid()}
                    >
                      {isSubmitting
                        ? isEditMode
                          ? "Updating..."
                          : "Saving..."
                        : isEditMode
                          ? "Update"
                          : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedProjectForSimilar(null);
          }}
          sourceId={selectedProjectForSimilar}
          sourceType="corporate_design_project"
        />
      )}
    </>
  );
}

export default OurProjectCorporateDesign;
